import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { EmailQueryDto, EmailQueryInterface } from '@zaoblako/shared/common/dtos/email-query.dto';
import { ValidationErrorInterface } from '@zaoblako/shared/common/interfaces/validation-error.interface';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import {
  ExternalUserCreateDto,
  ExternalUserCreateInterface
} from '@zaoblako/shared/modules/security/dtos/external-user/external-user-create.dto';
import { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { ProfileFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-find.dto';
import { ProfileFormInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-form.dto';
import {
  ProfileRegisterDto,
  ProfileRegisterInterface
} from '@zaoblako/shared/modules/security/dtos/profile/profile-register.dto';
import type { ExternalUserFormInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-form.dto';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ExternalUserService } from '@zaoblako/web/core/services/security/external-user.service';
import { ProfileService } from '@zaoblako/web/core/services/security/profile.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ContragentMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contragent-multi-select/contragent-multi-select.component';
import { ExternalUserPermissionsWidgetComponent } from '@zaoblako/web/modules/security/dumbs/external-user-permissions-widget/external-user-permissions-widget.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { MenuItem } from 'primeng/api';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { StepsModule } from 'primeng/steps';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-external-user-create-form',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SpinnerComponent,
    StepsModule,
    CheckboxModule,
    FormsModule,
    ContragentMultiSelectComponent,
    ClientAutoCompleteComponent,
    ExternalUserPermissionsWidgetComponent
  ],
  templateUrl: './external-user-create-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalUserCreateFormComponent extends BaseFormComponent<ExternalUserCreateInterface> implements OnInit {
  @Input() public readonly index: number;
  public email: EmailQueryInterface = <EmailQueryInterface>{};
  public errorEmail: ValidationErrorInterface<EmailQueryInterface>;

  public profile: ProfileFindInterface;
  public profileRegister: ProfileRegisterInterface = <ProfileRegisterInterface>{};
  public errorProfileRegister: ValidationErrorInterface<ProfileRegisterInterface>;

  public user = <ExternalUserCreateInterface>{ profile: {} };
  public dto = ExternalUserCreateDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public steps: MenuItem[] = [{ label: 'Email' }, { label: 'ФИО' }, { label: 'Доп. информация' }];
  public activeIndexStep = 0;

  public clients: ClientFindInterface[];

  public client: ClientFindInterface;
  public contragent: ContragentFindInterface;

  public constructor(
    private readonly externalUserService: ExternalUserService,
    private readonly profileService: ProfileService,
    private readonly formStoreService: FormStoreService,
    private readonly router: Router,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.client = this.formStoreService.getState.forms[this.index]?.data?.client;
    this.contragent = this.formStoreService.getState.forms[this.index]?.data?.contragent;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.client) {
      this.user.client = this.client;
    }

    if (this.contragent) {
      this.user.contragents = [this.contragent];
    }
  }

  public saveUser() {
    const closeForm = (user: ExternalUserFindInterface, withProfile = false) => {
      this.formStoreService.setLoading(false, this.index);
      if (!withProfile) {
        this.errorService.addSuccessMessage('Внешний пользователь создан');
      } else {
        this.errorService.addSuccessMessage(
          'Внешний пользователь создан',
          'Данный пользователь был найден в реестре системы, все его данные были автоматически заполнены'
        );
      }
      if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
        this.formStoreService.closeSidebar(this.index);
        this.router.navigate(['/client/external-user/card/' + user.id]).catch(console.error);
      } else {
        this.formStoreService.saveNext(user, this.index);
      }
    };

    if (!this.activeIndexStep) {
      const errors = this.validationService.parseAndValidate(this.email, EmailQueryDto)?.[1];
      if (errors) {
        this.errorEmail = errors.validationErrors;
        return;
      }

      this.formStoreService.setLoading(true, this.index);

      this.profileService.testEmail(this.email).subscribe({
        next: (data) => {
          if (data.externalUser) {
            this.errorService.addCustomError('Пользователь с таким email уже существует');
            this.formStoreService.setLoading(false, this.index);
            this.cdRef.markForCheck();
          } else if (data.profile) {
            this.profile = data.profile;
            [this.profileRegister.firstName, this.profileRegister.middleName, this.profileRegister.lastName] = [
              this.profile.firstName,
              this.profile.middleName,
              this.profile.lastName
            ];
            this.activeIndexStep = 1;
            this.formStoreService.setLoading(false, this.index);
            this.cdRef.markForCheck();
          } else {
            this.activeIndexStep = 1;
            this.formStoreService.setLoading(false, this.index);
            this.cdRef.markForCheck();
          }
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    } else if (this.activeIndexStep === 1) {
      this.profileRegister.email = this.email.email;
      if (
        this.profile?.id &&
        this.profile.name !==
          [this.profileRegister.lastName, this.profileRegister.firstName, this.profileRegister.middleName].join(' ')
      ) {
        this.profile.name = [
          this.profileRegister.lastName,
          this.profileRegister.firstName,
          this.profileRegister.middleName
        ].join(' ');
      }
      if (this.profile) {
        this.profile.email = this.email.email;
        this.profile.lastName = this.profile.name.split(' ')[0];
        this.profile.firstName = this.profile.name.split(' ')[1];
        this.profile.middleName = this.profile.name.split(' ')[2];
      }

      const errors = this.validationService.parseAndValidate(this.profileRegister, ProfileRegisterDto)?.[1];
      if (errors) {
        this.errorProfileRegister = errors.validationErrors;
        return;
      }

      this.formStoreService.setLoading(true, this.index);

      const req = this.profile?.id
        ? this.profileService.update<ProfileFormInterface, ProfileFindInterface>(this.profile)
        : this.profileService.create<ProfileFindInterface>(this.profileRegister);

      req.subscribe({
        next: (profile) => {
          this.profile = profile;
          this.activeIndexStep = 2;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
          console.log('2', this.profile);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    } else if (this.activeIndexStep === 2) {
      this.user.profile = this.profile;
      console.log('3', this.profile);
      console.log(this.user);
      if (this.checkValidate(this.user).valid) {
        this.formStoreService.setLoading(true, this.index);

        this.externalUserService.create<ExternalUserFindInterface>(this.user).subscribe({
          next: (user) => {
            closeForm(user);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public back() {
    this.activeIndexStep--;
  }

  public updatePermissions(user: ExternalUserFormInterface) {
    this.user.accessControl = user.accessControl;
  }
}
